import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tagsApi, Tag } from '../api/tags';
import { XMarkIcon, TagIcon } from '@heroicons/react/24/outline';

interface TagSelectorProps {
  selectedTagIds: number[];
  onChange: (tagIds: number[]) => void;
  entityType: 'lead' | 'account' | 'contact';
  entityId?: number;
}

const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTagIds,
  onChange,
  entityType,
  entityId,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { data: allTags = [], isLoading: tagsLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: tagsApi.getAll,
  });

  // Always use selectedTagIds from props - it's the source of truth
  // The parent component is responsible for loading tags when editing
  const currentSelectedIds = selectedTagIds;

  const toggleTag = (tagId: number) => {
    const newIds = currentSelectedIds.includes(tagId)
      ? currentSelectedIds.filter(id => id !== tagId)
      : [...currentSelectedIds, tagId];
    onChange(newIds);
  };

  const selectedTags = allTags.filter(tag => currentSelectedIds.includes(tag.id));

  if (tagsLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="animate-pulse h-8 w-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        Tags
      </label>
      
      {/* Selected Tags Display */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedTags.map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
              style={{
                backgroundColor: tag.color ? `${tag.color}20` : '#3B82F620',
                color: tag.color || '#3B82F6',
                border: `1px solid ${tag.color || '#3B82F6'}40`,
              }}
            >
              <span
                className="w-2 h-2 rounded-full mr-2"
                style={{ backgroundColor: tag.color || '#3B82F6' }}
              />
              {tag.name}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTag(tag.id);
                }}
                className="ml-2 hover:bg-black/10 rounded-full p-0.5"
                title="Remove tag"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Tag Selector Dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full input-premium flex items-center justify-between text-left"
        >
          <span className="flex items-center">
            <TagIcon className="h-4 w-4 mr-2 text-gray-400" />
            <span className="text-gray-500">
              {selectedTags.length > 0
                ? `${selectedTags.length} tag${selectedTags.length > 1 ? 's' : ''} selected`
                : 'Select tags...'}
            </span>
          </span>
          <svg
            className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={(e) => {
                e.preventDefault();
                setIsOpen(false);
              }}
            />
            <div 
              className="absolute z-20 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {allTags.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  No tags available. Create tags in the Tags page.
                </div>
              ) : (
                <div className="p-2">
                  {allTags.map((tag) => {
                    const isSelected = currentSelectedIds.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleTag(tag.id);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center hover:bg-gray-50 transition-colors ${
                          isSelected ? 'bg-indigo-50' : ''
                        }`}
                      >
                        <div
                          className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                          style={{ backgroundColor: tag.color || '#3B82F6' }}
                        />
                        <span className="flex-1">{tag.name}</span>
                        {isSelected && (
                          <svg
                            className="h-4 w-4 text-indigo-600"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TagSelector;

